import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faSave,
  faTimes,
  faEye,
  faEyeSlash,
  faLocationDot,
  faPhone,
  faClock,
  faMapMarkerAlt,
} from "@fortawesome/free-solid-svg-icons";
import { supabase } from "../../lib/supabase";

const LocationManager = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingLocation, setEditingLocation] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  const [formData, setFormData] = useState({
    title: "",
    address: "",
    phone: "",
    schedule: "",
    is_active: true,
  });

  useEffect(() => {
    loadLocations();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadLocations = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("locations")
        .select("*")
        .order("order_index", { ascending: true });

      if (error) throw error;
      setLocations(data || []);
    } catch (error) {
      console.error("Error loading locations:", error);
      showMessage("Eroare la încărcarea locațiilor", "error");
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (text, type = "success") => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => {
      setMessage("");
      setMessageType("");
    }, 3000);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const resetForm = () => {
    setFormData({
      title: "",
      address: "",
      phone: "",
      schedule: "",
      is_active: true,
    });
    setEditingLocation(null);
    setShowAddForm(false);
  };

  const handleAddLocation = async (e) => {
    e.preventDefault();
    try {
      const newOrderIndex =
        Math.max(...locations.map((l) => l.order_index || 0), 0) + 1;

      const { data, error } = await supabase
        .from("locations")
        .insert([
          {
            ...formData,
            order_index: newOrderIndex,
          },
        ])
        .select();

      if (error) throw error;

      setLocations([...locations, ...data]);
      resetForm();
      showMessage("Locație adăugată cu succes!");
    } catch (error) {
      console.error("Error adding location:", error);
      showMessage("Eroare la adăugarea locației", "error");
    }
  };

  const handleEditLocation = (location) => {
    setFormData({
      title: location.title || "",
      address: location.address || "",
      phone: location.phone || "",
      schedule: location.schedule || "",
      is_active: location.is_active !== false,
    });
    setEditingLocation(location.id);
    setShowAddForm(false);
  };

  const handleUpdateLocation = async (e) => {
    e.preventDefault();
    try {
      const { error } = await supabase
        .from("locations")
        .update(formData)
        .eq("id", editingLocation);

      if (error) throw error;

      setLocations(
        locations.map((l) =>
          l.id === editingLocation ? { ...l, ...formData } : l
        )
      );
      resetForm();
      showMessage("Locație actualizată cu succes!");
    } catch (error) {
      console.error("Error updating location:", error);
      showMessage("Eroare la actualizarea locației", "error");
    }
  };

  const handleDeleteLocation = async (locationId) => {
    if (!window.confirm("Sigur doriți să ștergeți această locație?")) return;

    try {
      const { error } = await supabase
        .from("locations")
        .delete()
        .eq("id", locationId);

      if (error) throw error;

      setLocations(locations.filter((l) => l.id !== locationId));
      showMessage("Locație ștearsă cu succes!");
    } catch (error) {
      console.error("Error deleting location:", error);
      showMessage("Eroare la ștergerea locației", "error");
    }
  };

  const handleToggleActive = async (locationId, currentStatus) => {
    try {
      const { error } = await supabase
        .from("locations")
        .update({ is_active: !currentStatus })
        .eq("id", locationId);

      if (error) throw error;

      setLocations(
        locations.map((l) =>
          l.id === locationId ? { ...l, is_active: !currentStatus } : l
        )
      );
      showMessage(`Locație ${!currentStatus ? "activată" : "dezactivată"}!`);
    } catch (error) {
      console.error("Error toggling location status:", error);
      showMessage("Eroare la schimbarea statusului", "error");
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Se încarcă...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div className="header-content">
          <FontAwesomeIcon icon={faMapMarkerAlt} className="header-icon" />
          <div>
            <h1>Manager Locații Cabinet</h1>
            <p>Gestionează locațiile unde se află cabinetele medicale</p>
          </div>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => setShowAddForm(true)}
        >
          <FontAwesomeIcon icon={faPlus} /> Adaugă Locație
        </button>
      </div>

      {message && (
        <div className={`alert alert-${messageType === "error" ? "danger" : "success"}`}>
          {message}
        </div>
      )}

      {(showAddForm || editingLocation) && (
        <div className="admin-form-container">
          <form onSubmit={editingLocation ? handleUpdateLocation : handleAddLocation}>
            <div className="form-header">
              <h3>
                {editingLocation ? "Editează Locație" : "Adaugă Locație Nouă"}
              </h3>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={resetForm}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="row">
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="form-label">Titlu locație</label>
                  <input
                    type="text"
                    className="form-control"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="ex: Centrul Medical Principal"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="form-label">Adresă completă</label>
                  <textarea
                    className="form-control"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    rows="2"
                    placeholder="ex: Str. Leonte Anastasievici Nr. 11 corp B, București"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Telefon</label>
                  <input
                    type="tel"
                    className="form-control"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="0725 596 895"
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Program</label>
                  <input
                    type="text"
                    className="form-control"
                    name="schedule"
                    value={formData.schedule}
                    onChange={handleInputChange}
                    placeholder="ex: Luni-Vineri: 08:00 – 21:00"
                  />
                </div>
              </div>
            </div>

            <div className="mb-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                />
                <label className="form-check-label">
                  Locație activă (vizibilă pe site)
                </label>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                <FontAwesomeIcon icon={faSave} />{" "}
                {editingLocation ? "Actualizează" : "Adaugă"}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={resetForm}
              >
                Anulează
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="admin-content">
        <div className="row g-3">
          {locations.map((location) => (
            <div key={location.id} className="col-lg-6 col-md-12">
              <div className="card h-100 location-card">
                <div className="card-header d-flex justify-content-between align-items-center py-2">
                  <span className={`badge ${location.is_active ? 'bg-success' : 'bg-secondary'}`}>
                    {location.is_active ? 'ACTIV' : 'INACTIV'}
                  </span>
                  <div className="location-action-buttons">
                    <button
                      className={`location-action-btn ${location.is_active ? 'btn-toggle-active' : 'btn-toggle-inactive'}`}
                      onClick={() => handleToggleActive(location.id, location.is_active)}
                      title={location.is_active ? "Dezactivează" : "Activează"}
                    >
                      <FontAwesomeIcon icon={location.is_active ? faEyeSlash : faEye} />
                    </button>
                    <button
                      className="location-action-btn btn-edit"
                      onClick={() => handleEditLocation(location)}
                      title="Editează"
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button
                      className="location-action-btn btn-delete"
                      onClick={() => handleDeleteLocation(location.id)}
                      title="Șterge"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>

                <div className="card-body p-4">
                  <h5 className="card-title mb-3">
                    <FontAwesomeIcon icon={faLocationDot} className="me-2 text-primary" />
                    {location.title}
                  </h5>
                  
                  <div className="location-details">
                    <div className="location-item mb-2">
                      <FontAwesomeIcon icon={faMapMarkerAlt} className="location-icon" />
                      <span>{location.address}</span>
                    </div>
                    
                    {location.phone && (
                      <div className="location-item mb-2">
                        <FontAwesomeIcon icon={faPhone} className="location-icon" />
                        <span>{location.phone}</span>
                      </div>
                    )}
                    
                    {location.schedule && (
                      <div className="location-item mb-2">
                        <FontAwesomeIcon icon={faClock} className="location-icon" />
                        <span>{location.schedule}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {locations.length === 0 && (
          <div className="col-12">
            <div className="text-center py-5">
              <FontAwesomeIcon icon={faMapMarkerAlt} size="3x" className="text-muted mb-3" />
              <h4>Nicio locație configurată</h4>
              <p className="text-muted">Adaugă prima locație pentru cabinetul medical.</p>
              <button
                className="btn btn-primary"
                onClick={() => setShowAddForm(true)}
              >
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Adaugă Prima Locație
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationManager; 